import React, { useState } from 'react';
import '../styles/faq.css';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQProps {
  title?: string;
  subtitle?: string;
  description?: string;
  sectionNumber?: string;
  faqs?: FAQItem[];
}

const FAQ: React.FC<FAQProps> = ({
  title = "Frequently Asked Questions",
  subtitle = "We're Here to Help",
  description = "Find answers to common questions about personal injury cases and working with our firm.",
  sectionNumber = "0004",
  faqs = [
    {
      id: "1",
      question: "How much does it cost to hire Muse Legal?",
      answer: "We work on a contingency fee basis for personal injury cases. This means you don't pay any upfront costs or attorney fees unless we win your case. Our fee is a percentage of the settlement or verdict we secure for you."
    },
    {
      id: "2",
      question: "How long will my case take?",
      answer: "The timeline varies depending on the complexity of your case. Simple cases may settle in a few months, while more complex cases involving serious injuries or disputed liability could take a year or more. We'll provide you with a realistic timeline during your consultation."
    },
    {
      id: "3",
      question: "What should I do immediately after an accident?",
      answer: "First, seek medical attention even if you don't think you're seriously injured. Document the scene with photos if possible, collect witness information, and report the incident to relevant authorities. Avoid discussing fault or signing any documents without consulting an attorney first."
    },
    {
      id: "4",
      question: "What compensation can I receive?",
      answer: "You may be entitled to compensation for medical expenses (past and future), lost wages, loss of earning capacity, pain and suffering, emotional distress, and property damage. The specific damages depend on the circumstances of your case."
    },
    {
      id: "5",
      question: "Do I need to go to court?",
      answer: "Most personal injury cases settle out of court through negotiation. However, if we can't reach a fair settlement, we're fully prepared to take your case to trial. We'll guide you through every step of the process regardless of which path your case takes."
    },
    {
      id: "6",
      question: "How do I know if I have a valid case?",
      answer: "If you've been injured due to someone else's negligence or wrongful actions, you likely have a valid case. During your free consultation, we'll evaluate the facts, assess the strength of your claim, and provide honest guidance on whether pursuing legal action makes sense."
    },
    {
      id: "7",
      question: "What is the statute of limitations?",
      answer: "In California, you generally have two years from the date of injury to file a personal injury lawsuit. However, there are exceptions and certain cases may have shorter deadlines. It's important to contact an attorney as soon as possible to protect your rights."
    },
    {
      id: "8",
      question: "Will I have to deal with insurance companies?",
      answer: "Once you hire us, we handle all communications with insurance companies on your behalf. Insurance adjusters often try to minimize payouts, but we know their tactics and will fight to ensure you receive fair compensation."
    }
  ]
}) => {
  const [openId, setOpenId] = useState<string | null>(null);

  const toggleFAQ = (id: string) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="faq-container">
      {/* Header Section */}
      <div className="faq-header">
        <div className="faq-subtitle-section">
          <h1 className="faq-subtitle">{subtitle}</h1>
          <p className="faq-description">{description}</p>
        </div>
      </div>

      {/* FAQ List */}
      <div className="faq-list">
        {faqs.map((faq) => (
          <div
            key={faq.id}
            className={`faq-item ${openId === faq.id ? 'open' : ''}`}
          >
            <button
              className="faq-question-button"
              onClick={() => toggleFAQ(faq.id)}
              aria-expanded={openId === faq.id}
            >
              <h3 className="faq-question">{faq.question}</h3>
              <span className={`faq-icon ${openId === faq.id ? 'open' : ''}`}>
                +
              </span>
            </button>
            <div className={`faq-answer-wrapper ${openId === faq.id ? 'open' : ''}`}>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="faq-cta">
        <h3 className="faq-cta-title">Still have questions?</h3>
        <p className="faq-cta-text">We're here to help. Contact us for a free consultation.</p>
        <a href="#contact" className="faq-cta-button">Get in Touch</a>
      </div>
    </div>
  );
};

export default FAQ;

